import React, { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const COLORS = {
  cpu: '#60A5FA',
  mem: '#34D399',
  disk: '#F59E0B',
  net: '#A78BFA',
};

function fmt(val, decimals = 1) {
  return val != null ? Number(val).toFixed(decimals) : '—';
}

function GaugeBar({ value, max = 100, color }) {
  const pct = Math.min(100, (value / max) * 100);
  const barColor =
    pct > 85 ? '#EF4444' : pct > 65 ? '#F59E0B' : color;
  return (
    <div className="w-full bg-gray-700 rounded-full h-1.5">
      <div
        className="h-1.5 rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, backgroundColor: barColor }}
      />
    </div>
  );
}

function MiniChart({ data, dataKey, color, unit = '%' }) {
  return (
    <ResponsiveContainer width="100%" height={80}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: -32, bottom: 0 }}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="t" hide />
        <YAxis domain={[0, 100]} hide />
        <Tooltip
          contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11, padding: '4px 8px' }}
          labelStyle={{ display: 'none' }}
          formatter={(v) => [`${fmt(v)}${unit}`, '']}
        />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#grad-${dataKey})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function InstanceMetricsPanel({ instance, onClose }) {
  const [points, setPoints] = useState([]);
  const [latest, setLatest] = useState(null);
  const [hours, setHours] = useState(1);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const intervalRef = useRef(null);
  const token = localStorage.getItem('access_token');

  const fetchMetrics = async () => {
    try {
      const res = await fetch(
        `${API_URL}/v1/instances/${instance.id}/metrics?hours=${hours}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();

      const pts = (data.points || []).map((p) => ({
        t: new Date(p.collected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cpu: Number(p.cpu_usage?.toFixed(1)),
        mem: Number(p.memory_usage?.toFixed(1)),
        disk: Number(p.disk_usage?.toFixed(1)),
        netIn: Number((p.network_in / 1024).toFixed(1)),   // KB/s
        netOut: Number((p.network_out / 1024).toFixed(1)),
      }));

      setPoints(pts);
      setLatest(data.latest || null);
      setNoData(pts.length === 0);
    } catch (e) {
      console.error('metrics fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchMetrics();
    intervalRef.current = setInterval(fetchMetrics, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [instance.id, hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const l = latest || {};

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <div>
            <h2 className="text-white font-semibold text-lg">{instance.name}</h2>
            <p className="text-gray-400 text-sm">
              {instance.instance_type} · {instance.ip_address || 'provisioning…'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded px-2 py-1"
            >
              <option value={1}>1h</option>
              <option value={6}>6h</option>
              <option value={24}>24h</option>
              <option value={72}>3d</option>
            </select>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            Loading metrics…
          </div>
        ) : noData ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500 gap-2">
            <span className="text-3xl">📊</span>
            <p>No metrics yet — agent data arrives within 60 s of first collection.</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Summary gauges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'CPU', val: l.cpu_usage, color: COLORS.cpu, unit: '%' },
                { label: 'Memory', val: l.memory_usage, color: COLORS.mem, unit: '%' },
                { label: 'Disk', val: l.disk_usage, color: COLORS.disk, unit: '%' },
                { label: 'Net In', val: l.network_in != null ? l.network_in / 1024 : null, color: COLORS.net, unit: ' KB/s' },
              ].map(({ label, val, color, unit }) => (
                <div key={label} className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <div className="text-gray-400 text-xs mb-1">{label}</div>
                  <div className="text-white text-2xl font-bold mb-2">
                    {val != null ? `${fmt(val)}${unit}` : '—'}
                  </div>
                  <GaugeBar value={val ?? 0} color={color} />
                </div>
              ))}
            </div>

            {/* Charts grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: 'CPU Usage %', key: 'cpu', color: COLORS.cpu },
                { title: 'Memory Usage %', key: 'mem', color: COLORS.mem },
                { title: 'Disk Usage %', key: 'disk', color: COLORS.disk },
              ].map(({ title, key, color }) => (
                <div key={key} className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <div className="text-gray-300 text-sm font-medium mb-2">{title}</div>
                  <MiniChart data={points} dataKey={key} color={color} />
                </div>
              ))}

              {/* Network — dual lines */}
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <div className="text-gray-300 text-sm font-medium mb-2">Network KB/s</div>
                <ResponsiveContainer width="100%" height={80}>
                  <LineChart data={points} margin={{ top: 4, right: 4, left: -32, bottom: 0 }}>
                    <XAxis dataKey="t" hide />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11, padding: '4px 8px' }}
                      labelStyle={{ display: 'none' }}
                    />
                    <Line type="monotone" dataKey="netIn" stroke={COLORS.net} strokeWidth={1.5} dot={false} name="In" isAnimationActive={false} />
                    <Line type="monotone" dataKey="netOut" stroke="#F472B6" strokeWidth={1.5} dot={false} name="Out" isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span style={{ color: COLORS.net }}>▬ In</span>
                  <span style={{ color: '#F472B6' }}>▬ Out</span>
                </div>
              </div>
            </div>

            {/* Full timeline chart */}
            {points.length > 3 && (
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <div className="text-gray-300 text-sm font-medium mb-3">CPU + Memory Timeline</div>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={points} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="t" stroke="#6B7280" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis domain={[0, 100]} stroke="#6B7280" tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip
                      contentStyle={{ background: '#111827', border: '1px solid #374151' }}
                      labelStyle={{ color: '#D1D5DB' }}
                    />
                    <Line type="monotone" dataKey="cpu" stroke={COLORS.cpu} strokeWidth={2} dot={false} name="CPU %" isAnimationActive={false} />
                    <Line type="monotone" dataKey="mem" stroke={COLORS.mem} strokeWidth={2} dot={false} name="Memory %" isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
